import { useCallback, useEffect, useRef, useState } from 'react';
import { adminFetchJson } from '../adminFetch';

export const DEFAULT_PAGE_SIZE = 50;

// Drop empty / false params so they don't appear in the query string.
function serialize(params) {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || v === false) return;
    out[k] = typeof v === 'boolean' ? String(v) : String(v);
  });
  return out;
}

/**
 * Offset-paginated fetch for admin lists (users, feedback). Returns the first
 * page and appends more via `loadMore`. Re-fetches from offset 0 whenever `url`
 * or `params` change (pass a debounced search term in `params`). Aborts any
 * superseded request so fast typing can't apply a stale page.
 *
 * @param {string} url      endpoint returning `{ items, total }`
 * @param {Object} params   query params (search / rating / exclude_devs …)
 * @param {number} [limit]  page size
 * @returns {{ items, total, loading, loadingMore, error, hasMore, loadMore, reload }}
 */
export default function useAdminList(url, params, limit = DEFAULT_PAGE_SIZE) {
  const [items, setItems]             = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]             = useState(null);

  const abortRef = useRef(null);
  const itemsRef = useRef([]);
  itemsRef.current = items;

  // Serialize the inputs so an inline `params` object doesn't retrigger fetches.
  const paramsKey = JSON.stringify(serialize(params));

  const fetchPage = useCallback((offset, append) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    const qs = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
      ...JSON.parse(paramsKey),
    }).toString();

    return adminFetchJson(`${url}?${qs}`, { signal: controller.signal })
      .then((d) => {
        const page = d.items || [];
        setTotal(d.total || 0);
        setItems((prev) => (append ? [...prev, ...page] : page));
        setLoading(false);
        setLoadingMore(false);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(e.message);
        setLoading(false);
        setLoadingMore(false);
      });
  }, [url, paramsKey, limit]);

  // (Re)load the first page whenever the query changes.
  useEffect(() => {
    fetchPage(0, false);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore) return;
    if (itemsRef.current.length >= total) return;
    fetchPage(itemsRef.current.length, true);
  }, [fetchPage, loading, loadingMore, total]);

  return {
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore: items.length < total,
    loadMore,
    reload: () => fetchPage(0, false),
  };
}
